import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { onboardingAPI, schoolsAPI } from '../../api';
import { API_BASE_URL } from '../../config';

// Icons as SVGs with updated colors to match the new design
const PersonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#4299e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const BusinessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#4299e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
    <line x1="12" y1="18" x2="12" y2="18"></line>
    <path d="M9 22v-4h6v4"></path>
    <line x1="8" y1="6" x2="16" y2="6"></line>
    <line x1="8" y1="10" x2="16" y2="10"></line>
    <line x1="8" y1="14" x2="16" y2="14"></line>
  </svg>
);

const TeacherIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#4299e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 12c-3 0-5.5-1.5-7-3 1.5-1.5 4-3 7-3s5.5 1.5 7 3c-1.5 1.5-4 3-7 3z"></path>
    <circle cx="12" cy="9" r="2"></circle>
    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
  </svg>
);

const AdminIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#4299e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const Onboarding = ({ onComplete, onSkip }) => {
  const { getAccessTokenSilently } = useAuth0(); // Get Auth0 access token function
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false); // Add loading state
  const [error, setError] = useState(null); // Add error state
  const [formData, setFormData] = useState({
    userType: 'individual',
    role: 'teacher',
    name: '',
    organizationName: '',
    schoolName: ''
  });
  
  // Log props for debugging
  console.log("Rendering Onboarding component");
  
  const updateFormData = (field, value) => {
    console.log(`Updating ${field} to:`, value);
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleSkip = () => {
    console.log("Skip clicked");
    if (typeof onSkip === 'function') {
      onSkip();
    }
  };
  
  // Add school creation function
  const createSchool = async (organization_id) => {
    try {
      console.log("Creating school for organization:", organization_id);
      
      // Get fresh token with updated organization claims
      const token = await getAccessTokenSilently({ cacheMode: 'off' });
      
      const schoolData = {
        name: formData.schoolName || 'Main School',
        organization_id: organization_id
      };
      
      // If the token still doesn't have org claims, try a different approach
      try {
        const school = await schoolsAPI.createSchool(schoolData, token);
        console.log("School created successfully:", school);
        return school;
      } catch (schoolError) {
        if (schoolError.message.includes('No organization information found in token')) {
          console.log('JWT token not updated yet, creating school directly with organization ID...');
          
          // Create school directly with explicit organization ID
          const response = await fetch(`${API_BASE_URL}/api/schools`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(schoolData)
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to create school');
          }
          
          const school = await response.json();
          console.log("School created successfully with direct API call:", school);
          return school;
        } else {
          throw schoolError;
        }
      }
    } catch (err) {
      console.error("Error creating school:", err);
      setError(`Failed to create school: ${err.message}`);
      throw err;
    }
  };
  
  // Create or update organization with custom name
  const updateOrganization = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      console.log("About to create/update organization with token:", token.substring(0, 15) + "...");
      
      const organizationData = {
        name: formData.organizationName || 'My Organization',
        type: 'school'
      };

      const userData = {
        name: formData.name || null
      };

      const surveyData = {
        userType: formData.userType,
        surveyRole: formData.role
      };
      
      console.log('Creating/updating organization with data:', organizationData);
      console.log('Updating user with data:', userData);
      console.log('Survey data:', surveyData);
      
      // Use the onboardingAPI instead of direct fetch
      const organization = await onboardingAPI.ensureOrganization(
        organizationData,
        userData,
        surveyData,
        token
      );
      
      console.log('Organization created/updated:', organization);
      return organization;
      
    } catch (err) {
      console.error("Error updating/creating organization:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const handleNext = async () => {
    console.log("Next clicked");
    
    if (activeStep === 4) {
      // Final step, complete onboarding and update organization + create school
      try {
        // Update the existing organization with custom name
        const orgData = await updateOrganization();
        
        // Create the school for this organization
        const schoolData = await createSchool(orgData.id);
        
        // Complete onboarding with survey data
        const token = await getAccessTokenSilently();
        const surveyData = {
          userType: formData.userType,
          surveyRole: formData.role
        };

        try {
          const completionData = await onboardingAPI.completeOnboarding(surveyData, token);
          console.log('✅ Onboarding completed successfully:', completionData);
        } catch (completionError) {
          console.error('Failed to complete onboarding:', completionError.message);
          // Don't fail the whole process if completion tracking fails
        }
        
        // Include both organization and school data
        const finalData = {
          ...formData,
          organization_id: orgData.id,
          schoolId: schoolData.id,
          schoolData: schoolData
        };
        
        // Call onComplete with the updated data
        if (typeof onComplete === 'function') {
          onComplete(finalData);
        }
      } catch (err) {
        // Error already set in updateOrganization or createSchool
        console.error("Failed to complete onboarding:", err);
      }
    } else {
      // Go to next step
      setActiveStep(prev => prev + 1);
    }
  };
  
  const handleBack = () => {
    console.log("Back clicked");
    setActiveStep(prev => prev - 1);
  };

  // Render different content based on active step
  const renderStepContent = () => {
    switch (activeStep) {
        case 0:
        return (
          <>
            <h2 style={{ 
              marginBottom: '24px', 
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: '600',
              color: '#1a202c',
              lineHeight: 1.3
            }}>
              Are you signing up as an individual or organization?
            </h2>
            
            <p style={{
              textAlign: 'center',
              fontSize: '16px',
              color: '#718096',
              marginBottom: '40px',
              fontWeight: '500'
            }}>
              Choose how you'll be using ArcAttend
            </p>
            
            <div className="option-buttons" style={{ 
              display: 'flex', 
              gap: '24px',
              justifyContent: 'center',
              width: '100%'
            }}>
              <div 
                className="option-card"
                style={{ 
                  padding: '32px 24px',
                  cursor: 'pointer',
                  background: formData.userType === 'individual' ? '#f0f8ff' : '#ffffff',
                  border: formData.userType === 'individual' ? '2px solid #4299e1' : '2px solid #e2e8f0',
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '180px',
                  transition: 'all 0.3s ease',
                  boxShadow: formData.userType === 'individual' ? '0 8px 25px rgba(66, 153, 225, 0.15)' : '0 4px 12px rgba(0,0,0,0.05)',
                  transform: formData.userType === 'individual' ? 'translateY(-2px)' : 'none'
                }}
                onClick={() => updateFormData('userType', 'individual')}
                onMouseOver={(e) => {
                  if (formData.userType !== 'individual') {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                  }
                }}
                onMouseOut={(e) => {
                  if (formData.userType !== 'individual') {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                  }
                }}
              >
                <PersonIcon />
                <span style={{ 
                  marginTop: '20px',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: formData.userType === 'individual' ? '#2d3748' : '#4a5568'
                }}>
                  Individual
                </span>
                <span style={{ 
                  marginTop: '8px',
                  fontSize: '14px',
                  color: '#718096',
                  textAlign: 'center',
                  lineHeight: 1.4
                }}>
                  Single teacher or personal use
                </span>
              </div>
              
              <div 
                className="option-card"
                style={{ 
                  padding: '32px 24px',
                  cursor: 'pointer',
                  background: formData.userType === 'organization' ? '#f0f8ff' : '#ffffff',
                  border: formData.userType === 'organization' ? '2px solid #4299e1' : '2px solid #e2e8f0',
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '180px',
                  transition: 'all 0.3s ease',
                  boxShadow: formData.userType === 'organization' ? '0 8px 25px rgba(66, 153, 225, 0.15)' : '0 4px 12px rgba(0,0,0,0.05)',
                  transform: formData.userType === 'organization' ? 'translateY(-2px)' : 'none'
                }}
                onClick={() => updateFormData('userType', 'organization')}
                onMouseOver={(e) => {
                  if (formData.userType !== 'organization') {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                  }
                }}
                onMouseOut={(e) => {
                  if (formData.userType !== 'organization') {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                  }
                }}
              >
                <BusinessIcon />
                <span style={{ 
                  marginTop: '20px',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: formData.userType === 'organization' ? '#2d3748' : '#4a5568'
                }}>
                  Organization
                </span>
                <span style={{ 
                  marginTop: '8px',
                  fontSize: '14px',
                  color: '#718096',
                  textAlign: 'center',
                  lineHeight: 1.4
                }}>
                  School district or institution
                </span>
              </div>
            </div>
          </>
        );        case 1:
        return (
          <>
            <h2 style={{ 
              marginBottom: '24px', 
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: '600',
              color: '#1a202c',
              lineHeight: 1.3
            }}>
              What's your role?
            </h2>
            
            <p style={{
              textAlign: 'center',
              fontSize: '16px',
              color: '#718096',
              marginBottom: '40px',
              fontWeight: '500'
            }}>
              Don't worry - you'll be assigned as an admin regardless
            </p>
            
            <div className="option-buttons" style={{ 
              display: 'flex', 
              gap: '24px',
              justifyContent: 'center',
              width: '100%'
            }}>
              <div 
                className="option-card"
                style={{ 
                  padding: '32px 24px',
                  cursor: 'pointer',
                  background: formData.role === 'teacher' ? '#f0f8ff' : '#ffffff',
                  border: formData.role === 'teacher' ? '2px solid #4299e1' : '2px solid #e2e8f0',
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '180px',
                  transition: 'all 0.3s ease',
                  boxShadow: formData.role === 'teacher' ? '0 8px 25px rgba(66, 153, 225, 0.15)' : '0 4px 12px rgba(0,0,0,0.05)',
                  transform: formData.role === 'teacher' ? 'translateY(-2px)' : 'none'
                }}
                onClick={() => updateFormData('role', 'teacher')}
                onMouseOver={(e) => {
                  if (formData.role !== 'teacher') {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                  }
                }}
                onMouseOut={(e) => {
                  if (formData.role !== 'teacher') {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                  }
                }}
              >
                <TeacherIcon />
                <span style={{ 
                  marginTop: '20px',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: formData.role === 'teacher' ? '#2d3748' : '#4a5568'
                }}>
                  Teacher
                </span>
                <span style={{ 
                  marginTop: '8px',
                  fontSize: '14px',
                  color: '#718096',
                  textAlign: 'center',
                  lineHeight: 1.4
                }}>
                  I teach classes and take attendance
                </span>
              </div>
              
              <div 
                className="option-card"
                style={{ 
                  padding: '32px 24px',
                  cursor: 'pointer',
                  background: formData.role === 'admin' ? '#f0f8ff' : '#ffffff',
                  border: formData.role === 'admin' ? '2px solid #4299e1' : '2px solid #e2e8f0',
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '180px',
                  transition: 'all 0.3s ease',
                  boxShadow: formData.role === 'admin' ? '0 8px 25px rgba(66, 153, 225, 0.15)' : '0 4px 12px rgba(0,0,0,0.05)',
                  transform: formData.role === 'admin' ? 'translateY(-2px)' : 'none'
                }}
                onClick={() => updateFormData('role', 'admin')}
                onMouseOver={(e) => {
                  if (formData.role !== 'admin') {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                  }
                }}
                onMouseOut={(e) => {
                  if (formData.role !== 'admin') {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                  }
                }}
              >
                <AdminIcon />
                <span style={{ 
                  marginTop: '20px',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: formData.role === 'admin' ? '#2d3748' : '#4a5568'
                }}>
                  Admin
                </span>
                <span style={{ 
                  marginTop: '8px',
                  fontSize: '14px',
                  color: '#718096',
                  textAlign: 'center',
                  lineHeight: 1.4
                }}>
                  I manage the organization and settings
                </span>
              </div>
            </div>
          </>
        );        case 2:
        return (
          <>
            <h2 style={{ 
              marginBottom: '32px', 
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: '600',
              color: '#1a202c',
              lineHeight: 1.3
            }}>
              What is your name?
            </h2>
            
            <div style={{
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              <div style={{
                position: 'relative',
                marginBottom: '8px'
              }}>
                <input 
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  placeholder="Enter your full name"
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    fontSize: '16px',
                    borderRadius: '12px',
                    border: '2px solid #e2e8f0',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#f8fafc',
                    fontWeight: '500'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4299e1';
                    e.target.style.backgroundColor = '#fff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(66, 153, 225, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.backgroundColor = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <p style={{
                fontSize: '14px',
                color: '#718096',
                textAlign: 'center'
              }}>
                This will be used to personalize your experience
              </p>
            </div>
          </>
        );        case 3:
        return (
          <>
            <h2 style={{ 
              marginBottom: '32px', 
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: '600',
              color: '#1a202c',
              lineHeight: 1.3
            }}>
              What is your Organization Name?
            </h2>
            
            <div style={{
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              <div style={{
                position: 'relative',
                marginBottom: '8px'
              }}>
                <input 
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => updateFormData('organizationName', e.target.value)}
                  placeholder="Enter your organization name"
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    fontSize: '16px',
                    borderRadius: '12px',
                    border: '2px solid #e2e8f0',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#f8fafc',
                    fontWeight: '500'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4299e1';
                    e.target.style.backgroundColor = '#fff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(66, 153, 225, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.backgroundColor = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <p style={{
                fontSize: '14px',
                color: '#718096',
                textAlign: 'center'
              }}>
                This could be your school district, company, or institution
              </p>
            </div>
          </>
        );        case 4:
        return (
          <>
            <h2 style={{ 
              marginBottom: '32px', 
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: '600',
              color: '#1a202c',
              lineHeight: 1.3
            }}>
              What is your School Name?
            </h2>
            
            <div style={{
              maxWidth: '400px',
              margin: '0 auto'
            }}>
              <div style={{
                position: 'relative',
                marginBottom: '8px'
              }}>
                <input 
                  type="text"
                  value={formData.schoolName}
                  onChange={(e) => updateFormData('schoolName', e.target.value)}
                  placeholder="Enter your school name"
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    fontSize: '16px',
                    borderRadius: '12px',
                    border: '2px solid #e2e8f0',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    backgroundColor: '#f8fafc',
                    fontWeight: '500'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4299e1';
                    e.target.style.backgroundColor = '#fff';
                    e.target.style.boxShadow = '0 0 0 3px rgba(66, 153, 225, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.backgroundColor = '#f8fafc';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <p style={{
                fontSize: '14px',
                color: '#718096',
                textAlign: 'center'
              }}>
                This will be your main school within the organization
              </p>
            </div>
          </>
        );
        
      default:
        return <p>Unknown step</p>;
    }
  };

  // Progress indicators
  const renderProgressDots = () => {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        marginBottom: '24px'
      }}>
        {[0, 1, 2, 3, 4].map((step) => (
          <div
            key={step}
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: step === activeStep ? '#4299e1' : '#e0e0e0',
              transition: 'all 0.3s ease'
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="onboarding-container" style={{ 
      width: '100%', 
      height: '100vh', 
      display: 'flex', 
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif'
    }}>
      <div className="onboarding-card" style={{ 
        maxWidth: '600px', 
        width: '90%', 
        padding: '32px', 
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        transition: 'all 0.3s ease'
      }}>
        <h1 style={{ 
          textAlign: 'center', 
          marginBottom: '24px',
          fontSize: '28px',
          fontWeight: '600',
          color: '#1a202c'
        }}>Let's Get Started</h1>
        
        {renderProgressDots()}
        
        <div className="content-section" style={{ 
          minHeight: '240px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          margin: '20px 0 32px'
        }}>
          {renderStepContent()}
        </div>
        
        {/* Display error message if present */}
        {error && (
          <div style={{
            padding: '16px',
            marginBottom: '16px',
            color: '#d32f2f',
            backgroundColor: '#ffebee',
            borderRadius: '8px',
            textAlign: 'center',
            border: '1px solid #ffcdd2'
          }}>
            <div style={{ marginBottom: '12px', fontWeight: '500' }}>
              {error}
            </div>
            {error.includes('You already belong to an organization') && (
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#4299e1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#3182ce'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#4299e1'}
              >
                Exit
              </button>
            )}
          </div>
        )}
        
        <div className="action-buttons" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginTop: '20px'
        }}>
          <button
            onClick={activeStep === 0 ? handleSkip : handleBack}
            style={{ 
              padding: '12px 24px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              background: 'white',
              fontSize: '16px',
              fontWeight: '500',
              color: '#666',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
            disabled={loading}
          >
            {activeStep === 0 ? 'Skip' : 'Back'}
          </button>
          
          <button
            onClick={handleNext}
            style={{ 
              padding: '12px 28px',
              border: 'none',
              borderRadius: '8px',
              background: '#4299e1',
              color: 'white',
              fontSize: '16px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              boxShadow: '0 4px 10px rgba(66, 153, 225, 0.3)',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = '#3182ce')}
            onMouseOut={(e) => !loading && (e.currentTarget.style.backgroundColor = '#4299e1')}
            disabled={loading}
          >
            {loading ? 'Processing...' : (activeStep === 4 ? 'Complete' : 'Next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;